import React, { useRef, useEffect, useCallback } from 'react';
import * as THREE from 'three';
import './DodecahedronPreview.scss';

const phi = (1 + Math.sqrt(5)) / 2;
const R = 1.3;

const RAW_VERTS: number[][] = [
  [1, 1, 1], [1, 1, -1], [1, -1, 1], [1, -1, -1],
  [-1, 1, 1], [-1, 1, -1], [-1, -1, 1], [-1, -1, -1],
  [0, phi, 1 / phi], [0, phi, -1 / phi], [0, -phi, 1 / phi], [0, -phi, -1 / phi],
  [1 / phi, 0, phi], [-1 / phi, 0, phi], [1 / phi, 0, -phi], [-1 / phi, 0, -phi],
  [phi, 1 / phi, 0], [phi, -1 / phi, 0], [-phi, 1 / phi, 0], [-phi, -1 / phi, 0],
];

const VERTS = RAW_VERTS.map(v => {
  const l = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
  return [v[0] / l * R, v[1] / l * R, v[2] / l * R];
});

const EDGE_THRESH = 2 * R / Math.sqrt(3) * 1.05;

function buildEdges(): [number, number][] {
  const edges: [number, number][] = [];
  for (let i = 0; i < 20; i++) {
    for (let j = i + 1; j < 20; j++) {
      const dx = VERTS[i][0] - VERTS[j][0];
      const dy = VERTS[i][1] - VERTS[j][1];
      const dz = VERTS[i][2] - VERTS[j][2];
      if (Math.sqrt(dx * dx + dy * dy + dz * dz) < EDGE_THRESH) {
        edges.push([i, j]);
      }
    }
  }
  return edges;
}

const FACES = [
  [0, 8, 9, 1, 16], [0, 12, 13, 4, 8], [0, 16, 17, 2, 12],
  [3, 14, 15, 7, 11], [3, 17, 16, 1, 14], [3, 11, 10, 2, 17],
  [5, 9, 8, 4, 18], [5, 15, 14, 1, 9], [5, 18, 19, 7, 15],
  [6, 13, 12, 2, 10], [6, 10, 11, 7, 19], [6, 19, 18, 4, 13],
];

function createParticleTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 64;
  c.height = 64;
  const ctx = c.getContext('2d')!;
  const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.1, 'rgba(255,255,255,0.7)');
  g.addColorStop(0.35, 'rgba(255,255,255,0.1)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  return new THREE.CanvasTexture(c);
}

interface FaceSpriteObj { sprite: THREE.Sprite; mat: THREE.SpriteMaterial; phase: number }
interface VertSpriteObj { sprite: THREE.Sprite; mat: THREE.SpriteMaterial; phase: number; baseSize: number }
interface FlowParticle {
  sprite: THREE.Sprite;
  mat: THREE.SpriteMaterial;
  path: { a: number[]; b: number[] } | null;
  progress: number;
  active: boolean;
  delay: number;
  speed: number;
  size: number;
  phase: number;
}

const IDLE_SPEED = 0.15;
const HOVER_SPEED = 1.0;
const LERP_RATE = 0.045;

export const DodecahedronPreview: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const targetSpeedRef = useRef(IDLE_SPEED);

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      45, container.clientWidth / container.clientHeight, 0.1, 100
    );
    camera.position.z = 6.5;

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);

    const group = new THREE.Group();
    scene.add(group);

    const particleTex = createParticleTexture();
    const edges = buildEdges();

    // Edge outlines
    edges.forEach(([i, j]) => {
      const pts = [new THREE.Vector3(...VERTS[i]), new THREE.Vector3(...VERTS[j])];
      group.add(new THREE.Line(
        new THREE.BufferGeometry().setFromPoints(pts),
        new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.18 })
      ));
    });

    // Internal connections (antipodal vertices through center)
    const internalConns: [number, number][] = [];
    for (let i = 0; i < 20; i++) {
      let maxDist = 0;
      let oppIdx = -1;
      for (let j = 0; j < 20; j++) {
        if (i === j) continue;
        const dx = VERTS[i][0] - VERTS[j][0];
        const dy = VERTS[i][1] - VERTS[j][1];
        const dz = VERTS[i][2] - VERTS[j][2];
        const d = dx * dx + dy * dy + dz * dz;
        if (d > maxDist) { maxDist = d; oppIdx = j; }
      }
      if (oppIdx > i) internalConns.push([i, oppIdx]);
    }

    internalConns.forEach(([i, j]) => {
      const pts = [
        new THREE.Vector3(...VERTS[i]),
        new THREE.Vector3(0, 0, 0),
        new THREE.Vector3(...VERTS[j]),
      ];
      group.add(new THREE.Line(
        new THREE.BufferGeometry().setFromPoints(pts),
        new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.2 })
      ));
    });

    // Face centers
    const faceCenters: number[][] = [];
    FACES.forEach(face => {
      let cx = 0, cy = 0, cz = 0;
      face.forEach(vi => { cx += VERTS[vi][0]; cy += VERTS[vi][1]; cz += VERTS[vi][2]; });
      cx /= face.length; cy /= face.length; cz /= face.length;
      const l = Math.sqrt(cx * cx + cy * cy + cz * cz);
      faceCenters.push([cx / l * R * 1.02, cy / l * R * 1.02, cz / l * R * 1.02]);
    });

    const faceSpriteData: FaceSpriteObj[] = [];
    faceCenters.forEach((fc, fi) => {
      const mat = new THREE.SpriteMaterial({
        map: particleTex, transparent: true, opacity: 0.25,
        blending: THREE.AdditiveBlending
      });
      const sprite = new THREE.Sprite(mat);
      sprite.position.set(fc[0], fc[1], fc[2]);
      sprite.scale.set(0.06, 0.06, 1);
      group.add(sprite);
      faceSpriteData.push({ sprite, mat, phase: fi * 0.7 });
    });

    // Face center to vertex connections
    FACES.forEach((face, fi) => {
      const fc = faceCenters[fi];
      if (!fc) return;
      face.forEach(vi => {
        const pts = [
          new THREE.Vector3(fc[0], fc[1], fc[2]),
          new THREE.Vector3(...VERTS[vi]),
        ];
        group.add(new THREE.Line(
          new THREE.BufferGeometry().setFromPoints(pts),
          new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.06 })
        ));
      });
    });

    // Vertex sprites
    const vertSprites: VertSpriteObj[] = [];
    VERTS.forEach((v, vi) => {
      const mat = new THREE.SpriteMaterial({
        map: particleTex, transparent: true, opacity: 0.5,
        blending: THREE.AdditiveBlending
      });
      const sprite = new THREE.Sprite(mat);
      sprite.position.set(v[0], v[1], v[2]);
      sprite.scale.set(0.1, 0.1, 1);
      group.add(sprite);
      vertSprites.push({ sprite, mat, phase: vi * 0.5, baseSize: 0.1 });
    });

    // Core glow
    const coreMat = new THREE.SpriteMaterial({
      map: particleTex, transparent: true, opacity: 0.35,
      blending: THREE.AdditiveBlending
    });
    const coreSprite = new THREE.Sprite(coreMat);
    coreSprite.scale.set(0.3, 0.3, 1);
    group.add(coreSprite);

    // Flow particles
    const allPaths = [
      ...edges.map(([i, j]) => ({ a: VERTS[i], b: VERTS[j] })),
      ...internalConns.map(([i, j]) => ({ a: VERTS[i], b: VERTS[j] })),
    ];

    const flowParticles: FlowParticle[] = [];
    for (let i = 0; i < 18; i++) {
      const mat = new THREE.SpriteMaterial({
        map: particleTex, transparent: true, opacity: 0,
        blending: THREE.AdditiveBlending
      });
      const sprite = new THREE.Sprite(mat);
      sprite.scale.set(0.08, 0.08, 1);
      group.add(sprite);
      flowParticles.push({
        sprite, mat,
        path: null, progress: 0, active: false,
        delay: i * 0.35,
        speed: 0.15 + Math.random() * 0.1,
        size: 0.06 + Math.random() * 0.04,
        phase: Math.random() * Math.PI * 2,
      });
    }

    const dpr = Math.min(window.devicePixelRatio, 2);
    let prevT = 0;
    const renderFrame = (t: number) => {
      const dt = t - prevT;
      prevT = t;

      const w = container.clientWidth;
      const h = container.clientHeight;
      if (w > 0 && h > 0 && (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr))) {
        renderer.setSize(w, h, false);
        canvas.style.width = w + 'px';
        canvas.style.height = h + 'px';
        camera.aspect = w / h;
        camera.position.z = 6.5 * (380 / Math.max(w, 220));
        camera.updateProjectionMatrix();
      }

      flowParticles.forEach(fp => {
        if (!fp.active && t > fp.delay) {
          fp.path = allPaths[Math.floor(Math.random() * allPaths.length)];
          fp.progress = 0;
          fp.active = true;
        }
        if (fp.active && fp.path) {
          fp.progress += fp.speed * dt;
          if (fp.progress >= 1) {
            fp.active = false;
            fp.delay = t + 0.15 + Math.random() * 0.5;
            fp.mat.opacity = 0;
          } else {
            const { a, b } = fp.path;
            fp.sprite.position.set(
              a[0] + (b[0] - a[0]) * fp.progress,
              a[1] + (b[1] - a[1]) * fp.progress,
              a[2] + (b[2] - a[2]) * fp.progress
            );
            fp.mat.opacity = 0.35 + Math.sin(fp.progress * Math.PI) * 0.2;
            const s = fp.size * (1 + Math.sin(t * 1.5 + fp.phase) * 0.2);
            fp.sprite.scale.set(s, s, 1);
          }
        }
      });

      vertSprites.forEach(vs => {
        vs.mat.opacity = 0.35 + Math.sin(t * 2 + vs.phase) * 0.2;
        const s = vs.baseSize * (1 + Math.sin(t * 1.5 + vs.phase) * 0.12);
        vs.sprite.scale.set(s, s, 1);
      });

      faceSpriteData.forEach(fs => {
        fs.mat.opacity = 0.15 + Math.sin(t * 2 + fs.phase) * 0.1;
      });

      coreMat.opacity = 0.25 + Math.sin(t * 2) * 0.1;

      group.rotation.y = t * 0.1;
      group.rotation.x = 0.4 + Math.sin(t * 0.05) * 0.15;

      renderer.render(scene, camera);
    };

    let time = 0;
    let speed = IDLE_SPEED;
    let lastTs = performance.now();
    let frameId = 0;

    const loop = () => {
      const now = performance.now();
      const delta = (now - lastTs) / 1000;
      lastTs = now;
      speed += (targetSpeedRef.current - speed) * LERP_RATE;
      const scaledDt = delta * speed;
      time += scaledDt;
      renderFrame(time);
      frameId = requestAnimationFrame(loop);
    };
    frameId = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(frameId);
      renderer.dispose();
      particleTex.dispose();
      coreMat.dispose();
      faceSpriteData.forEach(fs => fs.mat.dispose());
      vertSprites.forEach(vs => vs.mat.dispose());
      flowParticles.forEach(fp => fp.mat.dispose());
    };
  }, []);

  const handleMouseEnter = useCallback(() => {
    targetSpeedRef.current = HOVER_SPEED;
  }, []);

  const handleMouseLeave = useCallback(() => {
    targetSpeedRef.current = IDLE_SPEED;
  }, []);

  return (
    <div
      ref={containerRef}
      className="dodecahedron-preview"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <canvas ref={canvasRef} />
    </div>
  );
};
